import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Clock, Hourglass, Wallet, TrendingUp, PiggyBank } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './ProjectDashboard.css';

const ProjectDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/projects/${id}/dashboard`);
        if (response.data && response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load dashboard data.');
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Error fetching dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [id]);

  if (loading) {
    return <div className="loading-container">Loading Dashboard...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Oops! Something went wrong.</h2>
        <p>{error}</p>
        <button className="btn-back" onClick={() => navigate('/app/projects')}>Back to Projects</button>
      </div>
    );
  }

  if (!data) return null;

  const { project, team_members } = data;

  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U';
  };

  return (
    <div className="project-dashboard-container">
      <div className="dashboard-header">
        <h1>
          Dashboard: {project.project_name}
        </h1>
        <button className="btn-back" onClick={() => navigate('/app/projects')}>Back to Projects</button>
      </div>

      <div className="metrics-grid">
        {user?.role !== 'Client' && (
          <>
            <div className="metric-card card-blue">
              <div className="metric-bg-icon"><Clock size={120} /></div>
              <div className="metric-content">
                <div className="metric-icon">
                  <Clock size={24} />
                </div>
                <div className="metric-details">
                  <div className="metric-title">Estimated Hours</div>
                  <div className="metric-value">
                    {project.estimated_hours} <span className="metric-unit">hrs</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="metric-card card-purple">
              <div className="metric-bg-icon"><Hourglass size={120} /></div>
              <div className="metric-content">
                <div className="metric-icon">
                  <Hourglass size={24} />
                </div>
                <div className="metric-details">
                  <div className="metric-title">Actual Hours Spent</div>
                  <div className="metric-value">
                    {project.actual_hours_spent} <span className="metric-unit">hrs</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {(user?.role === 'Admin' || user?.role === 'Client') && (
          <>
            <div className="metric-card card-green">
              <div className="metric-bg-icon"><Wallet size={120} /></div>
              <div className="metric-content">
                <div className="metric-icon">
                  <Wallet size={24} />
                </div>
                <div className="metric-details">
                  <div className="metric-title">Project Budget</div>
                  <div className="metric-value">
                    <span className="metric-unit">₹</span>{project.project_budget.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="metric-card card-orange">
              <div className="metric-bg-icon"><TrendingUp size={120} /></div>
              <div className="metric-content">
                <div className="metric-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="metric-details">
                  <div className="metric-title">Budget Utilized</div>
                  <div className="metric-value">
                    <span className="metric-unit">₹</span>{project.budget_utilized.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="metric-card card-red">
              <div className="metric-bg-icon"><PiggyBank size={120} /></div>
              <div className="metric-content">
                <div className="metric-icon">
                  <PiggyBank size={24} />
                </div>
                <div className="metric-details">
                  <div className="metric-title">Remaining Budget</div>
                  <div className="metric-value">
                    <span className="metric-unit">₹</span>{project.remaining_budget.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="team-section">
        <h2>Team Member Contributions</h2>
        <div className="team-table-container">
          <table className="team-table">
            <thead>
              <tr>
                <th>Team Member</th>
                <th>Assigned Tasks</th>
                <th>Completed Tasks</th>
                {user?.role !== 'Client' && <th>Hours Worked</th>}
                <th>Contribution</th>
              </tr>
            </thead>
            <tbody>
              {team_members.map((member) => (
                <tr key={member.user_id}>
                  <td>
                    <div className="member-info">
                      {member.avatar ? (
                        <img src={member.avatar} alt={member.name} className="member-avatar" />
                      ) : (
                        <div className="member-avatar">{getInitials(member.name)}</div>
                      )}
                      <span className="member-name">{member.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="task-badge">{member.assignedTasks}</span>
                  </td>
                  <td>
                    <span className="task-badge success">{member.completedTasks}</span>
                  </td>
                  {user?.role !== 'Client' && <td>{member.hoursWorked} hrs</td>}
                  <td>
                    <div className="contribution-text">
                      <span>{member.contributionPercent}% of total time</span>
                    </div>
                    <div className="contribution-bar-container">
                      <div 
                        className="contribution-bar" 
                        style={{ width: `${member.contributionPercent}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
              {team_members.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', color: '#6b7280' }}>
                    No team members assigned or found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;
